import JoyFormControl from '@mui/joy/FormControl';
import JoyFormHelperText from '@mui/joy/FormHelperText';
import React, { ComponentProps, createContext, useContext } from 'react';
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseControllerProps,
  UseControllerReturn,
  UseFormStateReturn,
  useController,
} from 'react-hook-form';

// Create a Context with an undefined initial value but with the correct type
const FormControllerContext = createContext<UseControllerReturn | undefined>(
  undefined,
);

type FormControllerProviderProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = {
  children: React.ReactNode;
  controller: UseControllerReturn<T, TName>;
};

function FormControllerProvider<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({ children, controller }: FormControllerProviderProps<T, TName>) {
  // Memoize the controller object to prevent unnecessary re-renders
  const contextValue = React.useMemo(() => controller, [controller]);
  return (
    <FormControllerContext.Provider
      value={contextValue as unknown as UseControllerReturn}
    >
      {children}
    </FormControllerContext.Provider>
  );
}

export const useFormController = () => {
  const ctx = useContext(FormControllerContext);
  return ctx;
};

type OwnFormControlProps = {
  errorMessage?: React.ReactNode;
};
export type FormControlProps = Omit<
  React.ComponentProps<typeof JoyFormControl>,
  'children' | 'defaultValue'
> &
  OwnFormControlProps;

export type FormControllerProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = FormControlProps &
  Pick<
    UseControllerProps<T, TName>,
    'control' | 'defaultValue' | 'disabled'
  > & {
    shouldShowError?: boolean;
    field: TName;
    children:
      | ((props: {
          field: ControllerRenderProps<T, TName>;
          fieldState: ControllerFieldState;
          formState: UseFormStateReturn<T>;
        }) => React.ReactElement)
      | React.ReactNode;
  };

// Type to use when composing `FormController` with other components (e.g. `InputField`)
export type ComposedFormControllerProps<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
> = Pick<
  FormControllerProps<T, TName>,
  | 'color'
  | 'disabled'
  | 'required'
  | 'size'
  | 'control'
  | 'field'
  | 'defaultValue'
>;

export function FormController<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field: fieldName,
  errorMessage,
  size = 'md',
  defaultValue,
  shouldShowError = true,
  ...props
}: FormControllerProps<T, TName>) {
  const controller = useController<T, TName>({
    control,
    name: fieldName,
    defaultValue,
  });
  const isInvalid = props.error ?? controller.fieldState.invalid;
  const message = errorMessage ?? controller.fieldState.error?.message ?? '';

  return (
    <JoyFormControl {...props} size={size} error={isInvalid}>
      <FormControllerProvider controller={controller}>
        {typeof props.children === 'function'
          ? props.children(controller)
          : props.children}
        {shouldShowError && isInvalid && message && (
          <JoyFormHelperText>{message}</JoyFormHelperText>
        )}
      </FormControllerProvider>
    </JoyFormControl>
  );
}

export function FieldErrorMessage<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field: fieldName,
  ...props
}: Pick<FormControllerProps<T, TName>, 'field' | 'control'> &
  Omit<ComponentProps<typeof JoyFormHelperText>, 'children'>) {
  const controller = useController<T, TName>({
    control,
    name: fieldName,
  });
  const isInvalid = controller.fieldState.invalid;
  const message = controller.fieldState.error?.message ?? '';

  if (!isInvalid || !message) return null;
  return <JoyFormHelperText {...props}>{message}</JoyFormHelperText>;
}

export function HeadlessField<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field: fieldName,
  ...props
}: Pick<
  UseControllerProps<T, TName>,
  'control' | 'defaultValue' | 'disabled'
> & {
  field: TName;
  children:
    | ((props: {
        field: ControllerRenderProps<T, TName>;
        fieldState: ControllerFieldState;
        formState: UseFormStateReturn<T>;
      }) => React.ReactElement)
    | React.ReactNode;
}) {
  const controller = useController<T, TName>({ control, name: fieldName });

  return (
    <FormControllerProvider controller={controller}>
      {typeof props.children === 'function'
        ? props.children(controller)
        : props.children}
    </FormControllerProvider>
  );
}

export { FormController as Field, JoyFormControl as FormControl };
